// src/services/userService.ts
import { app } from "../firebase";
import { getFirestore, doc, getDoc, setDoc, updateDoc, serverTimestamp, type Timestamp } from "firebase/firestore";
import type { User } from "firebase/auth";
import { captureError } from "../lib/sentry";
import type { FirestoreTimestamp } from "../types/common";

const db = getFirestore(app);

/** ===== Roles ===== */
export type Role = "member" | "coach" | "admin" | "owner";

export type EmergencyContact = {
  name?: string;
  phone?: string;
  relationship?: string;
};

export type HealthProfile = {
  conditions?: string[];
  allergies?: string[];
  medications?: string[];
  injuries?: string[];
  contraindicatedExercises?: string[];
  physicianClearance?: {
    required?: boolean;
    date?: string;
  };
  notes?: string;
  consentToShare?: boolean;
  lastUpdated?: string;
};

export type UserProfile = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  role: Role;
  isActive: boolean;
  branchId?: string | null;
  createdAt?: FirestoreTimestamp;
  updatedAt?: FirestoreTimestamp;
  photoURL?: string | null;
  phone?: string | null;
  emergencyContact?: EmergencyContact | null;
  health?: HealthProfile | null;
  adminNotes?: string | null;
  membership?: unknown;
  [k: string]: unknown;
};

export function userDocRef(tenantId: string, uid: string) {
  return doc(db, `tenants/${tenantId}/users/${uid}`);
}

function normEmail(e?: string | null): string | null {
  return e ? e.toLowerCase() : null;
}

export async function ensureUserProfile(tenantId: string, authUser: User): Promise<UserProfile> {
  const ref = userDocRef(tenantId, authUser.uid);
  const snap = await getDoc(ref);

  const base = {
    uid: authUser.uid,
    email: normEmail(authUser.email),
    displayName: authUser.displayName ?? null,
    photoURL: authUser.photoURL ?? null,
  };

  try {
    if (!snap.exists()) {
      const toCreate: UserProfile = {
        uid: base.uid,
        email: base.email,
        displayName: base.displayName,
        role: "member",
        isActive: true,
        branchId: null,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
        photoURL: base.photoURL,
        phone: null,
        emergencyContact: null,
        health: null,
        adminNotes: null,
        lastLoginAt: serverTimestamp() as unknown as Timestamp,
      };
      await setDoc(ref, toCreate);
      if (import.meta.env.DEV)
        console.info("[ensureUserProfile] created:", `tenants/${tenantId}/users/${authUser.uid}`);
      return toCreate;
    }

    const current = snap.data() as Partial<UserProfile>;
    const patch: Partial<UserProfile> = {
      updatedAt: serverTimestamp() as unknown as Timestamp,
      lastLoginAt: serverTimestamp() as unknown as Timestamp,
    };
    // Solo completamos isActive en perfiles viejos que no lo tenían: si el staff
    // dio de baja a alguien, volver a loguearse no lo reactiva (las reglas lo impiden).
    if (current.isActive === undefined) patch.isActive = true;
    if (!current.email && base.email) patch.email = base.email;
    if (!current.displayName && base.displayName) patch.displayName = base.displayName;
    if (current.photoURL === undefined && base.photoURL) patch.photoURL = base.photoURL;

    // NO esperamos esta escritura: es solo "último ingreso" + completar datos
    // faltantes. Si el celular tiene problemas para escribir (mala señal,
    // almacenamiento trabado), antes el login quedaba colgado esperándola.
    // Entrar solo depende de haber LEÍDO el perfil.
    updateDoc(ref, patch).catch((err: unknown) => {
      console.warn("[ensureUserProfile] no se pudo actualizar lastLoginAt", err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: "ensureUserProfile:patch",
        code: (err as { code?: string })?.code ?? "unknown",
        uid: authUser.uid,
      });
    });
    // Devolvemos lo leído + los datos completados (sin los serverTimestamp, que
    // todavía no son fechas reales).
    const { updatedAt: _u, lastLoginAt: _l, ...filled } = patch;
    void _u;
    void _l;
    return { ...current, ...filled, uid: current.uid ?? authUser.uid } as UserProfile;
  } catch (e) {
    console.error("ensureUserProfile error", e, "path:", `tenants/${tenantId}/users/${authUser.uid}`);
    throw e;
  }
}

export async function getUserProfile(tenantId: string, uid: string): Promise<UserProfile | null> {
  const ref = userDocRef(tenantId, uid);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as UserProfile) : null;
}

export async function upsertUserProfile(tenantId: string, uid: string, partial: Partial<UserProfile>): Promise<void> {
  const ref = userDocRef(tenantId, uid);
  const snap = await getDoc(ref);
  const nowIso = new Date().toISOString();

  const patch: Partial<UserProfile> & { updatedAt: ReturnType<typeof serverTimestamp> } = {
    ...partial,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  };
  if (partial.health) {
    patch.health = {
      ...partial.health,
      lastUpdated: partial.health.lastUpdated ?? nowIso,
    };
  }

  try {
    if (!snap.exists()) {
      const toCreate: UserProfile = {
        uid,
        email: normEmail(partial.email ?? null),
        displayName: partial.displayName ?? null,
        role: "member",
        isActive: true,
        branchId: partial.branchId ?? null,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
        photoURL: partial.photoURL ?? null,
        phone: partial.phone ?? null,
        emergencyContact: patch.emergencyContact ?? null,
        health: patch.health ?? null,
        adminNotes: partial.adminNotes ?? null,
        lastLoginAt: serverTimestamp() as unknown as Timestamp,
      };
      await setDoc(ref, toCreate);
      return;
    }
    await updateDoc(ref, patch);
  } catch (e) {
    console.error("upsertUserProfile error", e, "path:", `tenants/${tenantId}/users/${uid}`);
    throw e;
  }
}

export function listenUserProfile(): never {
  throw new Error("Implementa onSnapshot si lo necesitás en esta app.");
}

export async function listUsers(
  tenantId: string,
  filters?: { role?: Role; isActive?: boolean }
): Promise<UserProfile[]> {
  const { collection, getDocs, query, orderBy } = await import("firebase/firestore");
  const colRef = collection(db, `tenants/${tenantId}/users`);

  const snapshot = await getDocs(query(colRef, orderBy("displayName", "asc")));
  // Incluir doc.id como uid por si el campo no está guardado en el documento
  let users = snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }) as UserProfile);

  if (filters?.isActive !== undefined) {
    users = users.filter((u) => u.isActive === filters.isActive);
  }
  if (filters?.role) {
    users = users.filter((u) => u.role === filters.role);
  }

  return users;
}
